// Canonical look for a page-level filter control — the Analytics top filter bar
// design: an 11px grey label above an unfilled 32px control with a #E2E2E2
// border, 12px value text and a 14px chevron.
//
// Shared so the Produce Planner toolbar and the Global Produce Map filter strip
// render the exact same chrome instead of each hand-rolling near-identical
// classes (they had drifted to 27px chips, #E8EBE9 borders, white fills and
// 16px chevrons). Width is deliberately excluded — every strip sizes its own
// columns — as is the height, which callers override only when they must match
// a neighbouring control (e.g. the mobile top bar's hamburger).

export const FILTER_LABEL_CLASS =
  "mb-[4px] block font-['Inter'] text-[11px] font-medium text-[#828382] dark:text-gray-400";

export const FILTER_CHEVRON_SIZE = 14;

export const FILTER_CHEVRON_CLASS =
  'ml-[6px] shrink-0 text-[#828382] transition-transform duration-200 dark:text-gray-400';

export const FILTER_VALUE_CLASS =
  "min-w-0 flex-1 truncate text-left font-['Inter'] text-[12px]";

export const FILTER_VALUE_TEXT_CLASS = 'text-[#282A29] dark:text-white';

export const FILTER_PLACEHOLDER_TEXT_CLASS = 'text-[#828382] dark:text-gray-400';

/**
 * Class string for the trigger button of a stacked filter control.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.disabled] - Dimmed, non-interactive (no hover accent).
 * @param {boolean} [opts.open] - Menu open; holds the green accent border.
 * @param {string} [opts.heightClass] - Overrides the default 32px height.
 */
export const filterTriggerClass = ({
  disabled = false,
  open = false,
  heightClass = 'h-[32px]',
  // `map` is the Global Produce Map's floating treatment: a filled 40px pill
  // that has to read against map tiles rather than a page background, so it is
  // taller, rounder and heavier-weighted than the default filter-bar control.
  // Analytics and Produce Planner keep the default.
  tone = 'default',
} = {}) => {
  if (tone === 'map') {
    const mapState = disabled
      ? 'cursor-not-allowed opacity-60'
      : open
        ? 'cursor-pointer border-bloom-green'
        : 'cursor-pointer border-[#E2E5E0] hover:border-bloom-green dark:border-[#2A302C] dark:hover:border-bloom-green';
    // 40px on desktop; the phone's map-controls row is 44px, so `heightClass`
    // is honoured here too rather than hard-coding one height.
    const mapHeight = heightClass === 'h-[32px]' ? 'h-[40px]' : heightClass;
    return `flex ${mapHeight} items-center justify-between gap-[16px] rounded-[8px] border bg-white px-[12px] font-['Inter'] text-[14px] font-semibold text-[#374151] transition-colors dark:bg-[#1B1F1C] dark:text-[#E6E8E6] ${mapState}`;
  }

  const state = disabled
    ? 'cursor-not-allowed border-[#E2E2E2] opacity-60 dark:border-[#3a3c3b]'
    : open
      ? 'cursor-pointer border-bloom-green'
      : 'cursor-pointer border-[#E2E2E2] hover:border-bloom-green dark:border-[#3a3c3b] dark:hover:border-bloom-green';

  return `flex ${heightClass} items-center justify-between rounded-[8px] border px-[12px] font-['Inter'] text-[12px] transition-colors ${state}`;
};
